import { create } from 'zustand';

import { CardStatus, CardType } from '@pflegia/types/common/job-position/card';

/**
 * Represents the possible states of the match dialogue modal.
 * - 'default': The default state. Defined as the state that shows the offer Modal.
 * - 'collectInfo': State for collecting additional information.
 * - 'superFastTrack': State for super fast track process.
 * - 'dateSuggestion': State for suggesting a date.
 * - 'whatsAppOptIn': State for WhatsApp opt-in, shown right after date suggestion.
 * - 'completed': State when the process is completed.
 * - 'confirmInvitation': State for confirming an invitation.
 */
type MatchDialogueStateType =
  | 'default'
  | 'collectInfo'
  | 'questionIntro'
  | 'checklist'
  | 'superFastTrack'
  | 'dateSuggestion'
  | 'whatsAppOptIn'
  | 'completed'
  | 'confirmInvitation';

/**
 * Data structure for the match dialogue modal store.
 */
export type MatchDialogueStoreData = {
  /** Whether the modal is open or closed. */
  open: boolean;
  /** The modal variant, e.g., Offer, Interview, etc. */
  state: MatchDialogueStateType;
  /** The status of the match, e.g., pending, accepted, declined, etc. Relevant for the offer Modal */
  status: CardStatus;
  /** The type of the match, e.g., application, interview, etc. Relevant for the offer Modal */
  type: CardType;
  /** The title of the modal (optional). */
  title?: string;
  /** The job position ID related to the match (optional). */
  jobPositionId?: string;
  /** The employer name related to the match (optional). */
  employerName?: string;
  /** The job position title related to the match (optional). */
  jobPositionTitle?: string;
  /** The match ID (optional). */
  matchId?: string;
  /** The invitation text (optional). */
  invitationText?: string | null;
  /** Whether the flow came from super fast track. */
  cameFromSuperFastTrack?: boolean;

  superFastTrackCandidate?: unknown;
  jobPosition?: unknown;

  /** Stores the previous state before opening, used for restoration on close without action */
  previousState?: Partial<MatchDialogueStoreData>;
  /** Internal resolver for confirmation dialogs. */
  confirmationResolver?: ((accepted: boolean) => void) | null;
};

/**
 * The default state for the match dialogue modal store.
 */
export const DEFAULT_STATE: MatchDialogueStoreData = {
  open: false,
  status: 'pending',
  type: 'application',
  title: undefined,
  jobPositionId: undefined,
  employerName: undefined,
  jobPositionTitle: undefined,
  matchId: undefined,
  invitationText: undefined,
  state: 'default',
  cameFromSuperFastTrack: false,
  superFastTrackCandidate: undefined,
  jobPosition: undefined,
  previousState: undefined,
} as const;

/**
 * Functions for the match dialogue modal store.
 */
export type MatchDialogueStoreCallbacks = {
  /** Opens the modal with the provided properties. */
  onOpen: (props: Partial<MatchDialogueStoreData>) => void;
  /** Closes the modal and resets the state to default. */
  onClose: () => void;
  /** Requests a confirmation from the modal and invokes a callback with the user's choice. */
  requestConfirmation: (props: Partial<MatchDialogueStoreData>, cb: (accepted: boolean) => void) => void;
  /** Resolves an outstanding confirmation request. */
  resolveConfirmation: (accepted: boolean) => void;
};

const DEFAULT_CALLBACKS: (
  set: (partial: Partial<MatchDialogueStoreState> | ((state: MatchDialogueStoreState) => Partial<MatchDialogueStoreState>)) => void,
) => MatchDialogueStoreCallbacks = set => ({
  onOpen: props =>
    set(state => {
      // Save current state before opening (excluding these fields from backup)
      // eslint-disable-next-line no-unused-vars
      const { previousState, open, ...currentState } = state;
      return {
        previousState: currentState,
        ...props,
        open: true,
      };
    }),
  onClose: () =>
    set(state => {
      // If there's an outstanding confirmation resolver, resolve it as 'false' (dismissed)
      const resolver = state.confirmationResolver;
      if (resolver) resolver(false);
      // If we have a previous state, restore it and clear the resolver
      if (state.previousState) {
        return {
          ...state.previousState,
          open: false,
          state: 'default',
          previousState: undefined,
          confirmationResolver: null,
        };
      }
      // Otherwise reset to default and clear the resolver
      return { ...state, state: 'default', open: false, confirmationResolver: null };
    }),
  requestConfirmation: (props, cb) =>
    set(state => ({
      ...state,
      ...props,
      open: true,
      state: 'confirmInvitation',
      confirmationResolver: cb,
    })),
  resolveConfirmation: accepted =>
    set(state => {
      const resolver = state.confirmationResolver;
      if (resolver) resolver(accepted);
      return {
        ...state,
        confirmationResolver: null,
        open: false,
        state: 'default',
        previousState: undefined,
      };
    }),
});

/**
 * The state and actions for the match dialogue modal store.
 */
export type MatchDialogueStoreState = (MatchDialogueStoreData & MatchDialogueStoreCallbacks) & {
  /** Sets a specific property in the store. e.g update open */
  set: <T extends keyof MatchDialogueStoreState>(key: T, value: MatchDialogueStoreState[T]) => void;
  /** Adds a callback behavior right after the execution to the default ones. e.g. adds a callback to execute after onOpen */
  setCallback: <T extends keyof MatchDialogueStoreCallbacks>(
    key: T,
    value: MatchDialogueStoreCallbacks[T],
    executionOrder: 'before' | 'after',
  ) => void;
  /** Sets multiple properties in the store, specifically for match information. */
  setMatchInfo: (matchInfo: Partial<MatchDialogueStoreData>) => void;
};

/**
 * Hook for the match dialogue modal state and actions.
 */
export const useMatchDialogueStore = create<MatchDialogueStoreState>((set, get) => {
  const defaultCallbacks: MatchDialogueStoreCallbacks = DEFAULT_CALLBACKS(set);
  return {
    ...DEFAULT_STATE,
    ...defaultCallbacks,
    set: (key, value) => set(() => ({ [key]: value })),
    setCallback: (key, value, executionOrder = 'after') =>
      set(() => ({
        [key]: (...args: unknown[]) => {
          const callIfFn = (fn?: unknown) => (typeof fn === 'function' ? fn(...args) : undefined);

          if (executionOrder === 'before') callIfFn(defaultCallbacks[key]);

          callIfFn(value);
          get();
          if (executionOrder === 'after') callIfFn(defaultCallbacks[key]);
        },
      })),
    setMatchInfo: matchInfo =>
      set(() => ({
        ...matchInfo,
      })),
  };
});
